import {
  CloseCircleOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Space, Table } from 'antd';
import { useSearchParams } from 'react-router-dom';

import {
  Error403Unauthorized,
  MultipleSelect,
  CustomAlert,
  EmptyComponentWithDataTestId,
} from '@app/components';
import { EmployeeSetupHintModal } from '@app/components/Modals/EmployeeSetupHintModal/EmployeeSetupHintModal';
import { useEmployeeSetupHintModal } from '@app/components/Modals/EmployeeSetupHintModal/hooks/useEmployeeSetupHintModal';
import {
  OFF,
  EMPLOYEES_HEADER,
  ERROR,
  PREFIX,
  minLength,
  maxLength,
  EDIT_EMPLOYEE_PATH,
} from '@app/constants';
import {
  RIGHTS_IS_ON,
  RIGHTS_WORK_SCHEDULES,
} from '@app/constants/accessRights';
import { useAccessRights, useUsersAccessRights } from '@app/hooks';
import {
  adaptDataToSelect,
  getPaginationItemRenderWithDataTestIds,
} from '@app/utils';

import { useEmployees } from '@pages/Employees/EmployeesList/hooks/useEmployees';

import { statusOptions } from '@pages/Employees/EmployeesList/data/statusOptions';
import { adaptDataToTable } from '@pages/Employees/EmployeesList/utils/adaptDataToTable';

import type { FC } from 'react';

import '@pages/Employees/EmployeesList/styles/Employees.desktop.scss';
import '@pages/Employees/EmployeesList/styles/Employees.tablet.scss';
import '@pages/Employees/EmployeesList/styles/Employees.mobile.scss';

const validationRules = {
  search: [minLength(), maxLength()],
};

export const EmployeesList: FC = () => {
  const {
    isLoading,
    form,
    submitForm,
    selectedStatus,
    setSelectedStatus,
    positions,
    selectedPositions,
    setSelectedPositions,
    allBranches,
    selectedBranches,
    setSelectedBranches,
    employees,
    handleEdit,
    totalRowsCount,
    pageNumber,
    rowsPerPage,
    handleChange,
    handleReset,
    searchError,
    setSearchError,
    navigate,
    isMobile,
  } = useEmployees();
  const { hasUserRightToEmployeeRegistry, hasUserRightToEdit } =
    useUsersAccessRights();
  const { hasUserRight: hasRightToWorkSchedule } = useAccessRights(
    RIGHTS_WORK_SCHEDULES,
    RIGHTS_IS_ON
  );

  const [params] = useSearchParams();
  const employeeId = params.get('employeeId');

  const { isHintModalOpen, closeHintModal } =
    useEmployeeSetupHintModal(employeeId);

  if (!hasUserRightToEmployeeRegistry) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}employees`}>
      <div className="header-container">
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={submitForm}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
        >
          <Form.Item
            name="search"
            wrapperCol={{ span: 16 }}
            rules={validationRules.search}
          >
            <Input
              data-testid="search-input"
              placeholder="Поиск (по имени, телефону, email)"
              addonBefore={<SearchOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space
              align="end"
              size="large"
              direction="horizontal"
              wrap
              style={{ width: '100%' }}
              className="toolbar-space"
            >
              <Space
                size="large"
                direction="horizontal"
                wrap
                style={{ width: '100%' }}
                className="selector-space"
              >
                <MultipleSelect
                  className="user-selector"
                  placeholder="Все филиалы"
                  allOptions={adaptDataToSelect(allBranches)}
                  width={100}
                  selectedOption={selectedBranches}
                  defaultSelected={true}
                  setSelectedOptions={setSelectedBranches}
                  dataTestId="branch-select"
                  optionDataTestId="branch-item"
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все должности"
                  allOptions={adaptDataToSelect(positions)}
                  width={100}
                  selectedOption={selectedPositions}
                  setSelectedOptions={setSelectedPositions}
                  dataTestId="position-select"
                  optionDataTestId="position-item"
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все статусы"
                  allOptions={statusOptions}
                  width={100}
                  selectedOption={selectedStatus}
                  setSelectedOptions={setSelectedStatus}
                  dataTestId="status-select"
                  optionDataTestId="status-item"
                  filterName="filterName"
                />

                <Form.Item
                  style={{
                    display: 'inline-block',
                    marginBottom: 0,
                  }}
                  className="buttons-container"
                >
                  {searchError && (
                    <CustomAlert
                      errorType={ERROR}
                      serverError={searchError}
                      setServerError={setSearchError}
                    />
                  )}
                  <Space className="tablet-buttons" size="middle">
                    <Button
                      htmlType="reset"
                      onClick={handleReset}
                      className="button"
                      data-testid="reset-search"
                    >
                      <CloseCircleOutlined />
                      Сброс
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className="button"
                      data-testid="show-search"
                    >
                      <FilterOutlined />
                      Поиск
                    </Button>
                  </Space>
                </Form.Item>
              </Space>

              <Space className="desktop-buttons" size="middle">
                <Button
                  htmlType="reset"
                  onClick={handleReset}
                  data-testid="reset-search"
                >
                  <CloseCircleOutlined />
                  Сброс
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  data-testid="show-search"
                >
                  <FilterOutlined />
                  Поиск
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        rowClassName="employee-item"
        columns={EMPLOYEES_HEADER}
        loading={isLoading}
        dataSource={adaptDataToTable(
          employees,
          handleEdit,
          navigate,
          hasRightToWorkSchedule,
          hasUserRightToEdit
        )}
        locale={{
          emptyText: <EmptyComponentWithDataTestId />,
        }}
        pagination={{
          itemRender: getPaginationItemRenderWithDataTestIds,
          size: isMobile ? 'small' : 'default',
          showSizeChanger: true,
          total: totalRowsCount,
          defaultCurrent: pageNumber,
          current: pageNumber,
          pageSize: rowsPerPage,
          position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
          onChange: handleChange,
          showTotal: (total, range) => (
            <span data-testid="pagination-info">{`Показаны ${range[0]}-${range[1]} из ${total}`}</span>
          ),
        }}
      />

      {employeeId && (
        <EmployeeSetupHintModal
          isOpen={isHintModalOpen}
          onClose={closeHintModal}
          accessRightsPath={`${EDIT_EMPLOYEE_PATH}?id=${employeeId}&key=8`}
          servicesPath={`${EDIT_EMPLOYEE_PATH}?id=${employeeId}&key=5`}
        />
      )}
    </div>
  );
};
